/**
 * Single-fetch, promise-cached loader for /aws-exports.json.
 *
 * Every caller (auth provider, chat runtime wiring, feedback service) needs a
 * slice of the same file. Without this shared loader each caller issues its
 * own fetch on first render, so the file gets hit 3+ times at boot. Going
 * through this module collapses that to one network round-trip.
 */

export type AwsExportsConfig = {
  // Cognito / OIDC
  authority?: string
  client_id?: string
  redirect_uri?: string
  post_logout_redirect_uri?: string
  response_type?: string
  scope?: string
  automaticSilentRenew?: boolean

  // AgentCore runtime
  agentRuntimeArn?: string
  awsRegion?: string
  agentPattern?: string

  // Application API (feedback + chat history)
  appApiUrl?: string
  /** @deprecated use `appApiUrl`. Removed in a follow-up cleanup. */
  feedbackApiUrl?: string
}

let configCache: AwsExportsConfig | null = null
let configPromise: Promise<AwsExportsConfig> | null = null

export async function loadAwsExports(): Promise<AwsExportsConfig> {
  if (configCache) {
    return configCache
  }
  if (configPromise) {
    return configPromise
  }

  configPromise = (async () => {
    try {
      const response = await fetch("/aws-exports.json")
      if (!response.ok) {
        throw new Error(`Failed to load aws-exports.json: ${response.status}`)
      }
      // A missing file does not reliably 404: dev and hosting SPA fallbacks
      // answer 200 with index.html, which then explodes as a JSON parse error
      // that names neither the file nor the fix. Detect that case and say
      // what is actually wrong.
      const contentType = response.headers.get("content-type") ?? ""
      if (!contentType.includes("json")) {
        throw new Error(
          "aws-exports.json is missing (the server returned a non-JSON fallback). " +
            "Generate it from the stack outputs: see docs/how-to/running-locally.md."
        )
      }
      const config = (await response.json()) as AwsExportsConfig
      configCache = config
      return config
    } finally {
      // Drop the in-flight promise so a failed load can be retried.
      // configCache stays set only on success via the assignment above.
      configPromise = null
    }
  })()

  return configPromise
}
